import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { notifySearchFailure } from '../../../utils/searchFailureToast';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import rootStore, { IMobxRootState } from '../../../RootStore';
import { IActiveCountry, IPrediction, VIEW } from '../../../interfaces';
import APIService from '../../../services/APIService';
import { PickerDrawer } from '../../Pickers/PickerDrawer';
import Predictions from './Predictions';
import { first, get, isEmpty } from 'lodash/fp';
import { MOMENTJS_DATE_DISPLAY_FORMAT } from '../../../constants';
import { useNavigate, useMatch } from 'react-router-dom';
import { E_TAB_FILTER } from './ActivityPageInterfaces';
import { getLastItemDate } from './activityUtils';

type Props = {
    selectedCountries?: number[];
    maxDate?: moment.Moment;
    minDate?: moment.Moment;
    licensedCountriesArr?: IActiveCountry[];
    searchString?: string;
};

const PER_PAGE = 30;

const PredictionsProvider = inject(
    ({ predictionsFeedStore, countryStore }: IMobxRootState) => ({
        selectedCountries: predictionsFeedStore.selectedCountries,
        maxDate: predictionsFeedStore.maxDate,
        minDate: predictionsFeedStore.minDate,
        licensedCountriesArr: countryStore.countries.licensedCountriesArr,
        searchString: predictionsFeedStore.searchString,
    })
)(
    observer(
        ({
            selectedCountries,
            maxDate,
            minDate,
            licensedCountriesArr,
            searchString,
        }: Props) => {
            const [predictions, setPredictions] = useState<IPrediction[]>([]);
            const [loading, setLoading] = useState(true);
            const [loadFailed, setLoadFailed] = useState(false);
            const [hasMore, setHasMore] = useState(true);
            const mountedRef = useRef(true);
            const predictionsRef = useRef<IPrediction[]>([]);
            const loadingRef = useRef(false);
            const navigate = useNavigate();
            const updatePredictions = useCallback(
                (newPredictions: IPrediction[]) => {
                    predictionsRef.current = newPredictions;
                    setPredictions(newPredictions);
                },
                []
            );
            const fetchPredictions = useCallback(
                async (reset: boolean) => {
                    if (loadingRef.current) return;
                    loadingRef.current = true;
                    setLoading(true);
                    setLoadFailed(false);
                    const current = reset ? [] : predictionsRef.current;
                    if (reset) updatePredictions([]);
                    try {
                        const maxDateParam = getLastItemDate(
                            current,
                            E_TAB_FILTER.PREDICTIONS
                        );
                        let results: IPrediction[];
                        if (searchString || !isEmpty(selectedCountries)) {
                            results = (await APIService.searchActivities(
                                E_TAB_FILTER.PREDICTIONS,
                                {
                                    maxDate: maxDateParam || maxDate?.format('YYYY-MM-DD'),
                                    startDate: minDate?.format('YYYY-MM-DD'),
                                    limit: PER_PAGE,
                                    searchString: searchString || '',
                                    countryIds: selectedCountries,
                                }
                            )) as IPrediction[];
                        } else {
                            results = await APIService.getPredictions({
                                limit: PER_PAGE,
                                maxDate: maxDate?.format('YYYY-MM-DD'),
                                startDate: minDate?.format('YYYY-MM-DD'),
                            });
                        }
                        if (!mountedRef.current) return;
                        const updated = reset
                            ? results
                            : [...current, ...results];
                        updatePredictions(updated);
                        setHasMore(results.length >= PER_PAGE);
                    } catch (e) {
                        console.error('Failed to fetch predictions:', e);
                        if (!mountedRef.current) return;
                        if (searchString) {
                            notifySearchFailure('predictions', () => (rootStore.predictionsFeedStore.searchString = ''));
                        } else {
                            setLoadFailed(true);
                        }
                    } finally {
                        loadingRef.current = false;
                        if (mountedRef.current) setLoading(false);
                    }
                },
                [maxDate, minDate, searchString, selectedCountries, updatePredictions]
            );
            useEffect(() => {
                updatePredictions([]);
                setHasMore(true);
                fetchPredictions(true);
            }, [maxDate, minDate, searchString, selectedCountries]);
            useEffect(() => {
                return () => {
                    mountedRef.current = false;
                };
            }, []);
            const loadMore = useCallback(() => {
                if (!loadingRef.current && hasMore) {
                    fetchPredictions(false);
                }
            }, [hasMore, fetchPredictions]);
            const predictionsWithDateString = useMemo(
                () =>
                    predictions.map(prediction => ({
                        ...prediction,
                        dateString: moment(prediction.call_date).format(
                            MOMENTJS_DATE_DISPLAY_FORMAT
                        ),
                    })),
                [predictions]
            );
            const match = useMatch(
                `/${VIEW.RISK_INTELLIGENCE}/:insightsView/:id/`
            );
            const hasIdInUrl = !!match?.params?.id;
            useEffect(() => {
                rootStore.predictionsFeedStore.currentIds = predictionsWithDateString
                    .map(p => (p.id !== null ? String(p.id) : null))
                    .filter(Boolean) as string[];
            }, [predictionsWithDateString]);
            useEffect(() => {
                return () => {
                    rootStore.predictionsFeedStore.currentIds = [];
                };
            }, []);
            useEffect(() => {
                if (!hasIdInUrl && !isEmpty(predictionsWithDateString)) {
                    navigate(
                        `/${VIEW.RISK_INTELLIGENCE}/predictions/${get(
                            'id',
                            first(predictionsWithDateString)
                        )}`,
                        { replace: true }
                    );
                }
            }, [hasIdInUrl, predictionsWithDateString, navigate]);
            const onPredictionClick = useCallback(
                (id: number) => {
                    navigate(`/${VIEW.RISK_INTELLIGENCE}/predictions/${id}`);
                },
                [navigate]
            );
            return (
                <PickerDrawer title="Predictions" side="left" defaultOpen>
                    <Predictions
                        predictions={predictionsWithDateString}
                        isLoading={loading}
                        loadFailed={loadFailed}
                        loadMore={loadMore}
                        licensedCountriesArr={licensedCountriesArr}
                        onPredictionClick={onPredictionClick}
                        maxDate={maxDate}
                        minDate={minDate}
                        selectedCountries={selectedCountries}
                        searchString={searchString}
                        onSetSearchString={str =>
                            (rootStore.predictionsFeedStore.searchString = str)
                        }
                        removeCountry={countryId =>
                            rootStore.predictionsFeedStore.removeCountry(
                                countryId
                            )
                        }
                        addCountry={countryId =>
                            rootStore.predictionsFeedStore.addCountry(countryId)
                        }
                        toggleCountry={countryId =>
                            rootStore.predictionsFeedStore.toggleCountry(
                                countryId
                            )
                        }
                        onDateRangeChange={(startDate, endDate) => {
                            rootStore.predictionsFeedStore.minDate = startDate
                                ? moment(startDate)
                                : null;
                            rootStore.predictionsFeedStore.maxDate = endDate
                                ? moment(endDate)
                                : null;
                        }}
                        onDateRangeReset={() =>
                            rootStore.predictionsFeedStore.resetDateFilter()
                        }
                        activeId={match?.params?.id}
                    />
                </PickerDrawer>
            );
        }
    )
);

export default PredictionsProvider;
